/**
 * CMP Manager - Gestion de la barre de consentement Pandectes
 * Cache le bouton "Refuser" uniquement au premier consentement
 * Cache l'élément de révocation et permet de réouvrir la bannière via data-cmp-open
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'cmp_first_consent_processed';
  const DENY_BUTTON_SELECTOR = '.cc-btn.cc-btn-decision.cc-deny';
  const ACCEPT_BUTTON_SELECTOR = '.cc-btn.cc-btn-decision.cc-allow';
  const REVOKE_ELEMENT_SELECTOR = '.cc-revoke.cc-bottom.cc-left';
  const CMP_OPEN_ATTRIBUTE = 'data-cmp-open';
  
  /**
   * Vérifie si le premier consentement a déjà été traité
   */
  function isFirstConsentProcessed() {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }

  /**
   * Marque le premier consentement comme traité
   */
  function markFirstConsentProcessed() {
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  /**
   * Cache le bouton de refus
   */
  function hideDenyButton() {
    const denyButton = document.querySelector(DENY_BUTTON_SELECTOR);
    if (denyButton) {
      denyButton.style.display = 'none';
      // Ajouter un attribut pour faciliter le débogage
      denyButton.setAttribute('data-cmp-hidden', 'true');
    }
  }

  /**
   * Affiche le bouton de refus (si il était caché)
   */
  function showDenyButton() {
    const denyButton = document.querySelector(DENY_BUTTON_SELECTOR);
    if (denyButton && denyButton.hasAttribute('data-cmp-hidden')) {
      denyButton.style.display = '';
      denyButton.removeAttribute('data-cmp-hidden');
    }
  }

  /**
   * Cache l'élément de révocation (bouton pour rouvrir la bannière)
   */
  function hideRevokeElement() {
    const revokeElement = document.querySelector(REVOKE_ELEMENT_SELECTOR);
    if (revokeElement) {
      revokeElement.style.display = 'none';
      revokeElement.style.visibility = 'hidden';
      revokeElement.style.opacity = '0';
      revokeElement.setAttribute('data-cmp-hidden', 'true');
    }
  }

  /**
   * Trouve l'élément de révocation
   */
  function getRevokeElement() {
    return document.querySelector(REVOKE_ELEMENT_SELECTOR);
  }

  /**
   * Ouvre la bannière de consentement en simulant le clic sur l'élément de révocation
   * Cette fonction est exposée globalement pour être utilisée depuis d'autres scripts
   */
  function openCMPBanner() {
    const revokeElement = getRevokeElement();
    if (revokeElement) {
      // Avant d'ouvrir, vérifier si on doit réafficher le bouton "Refuser"
      // Si le localStorage a été supprimé (après acceptation), on doit réafficher le bouton
      if (!isFirstConsentProcessed()) {
        // Attendre un peu que la bannière s'ouvre puis réafficher le bouton
        setTimeout(function() {
          showDenyButton();
        }, 100);
      }
      
      // Utiliser directement .click() qui simule un clic réel
      revokeElement.click();
      return true;
    } else {
      console.warn('CMP Manager: L\'élément de révocation n\'a pas été trouvé');
      return false;
    }
  }

  /**
   * Initialise les event listeners pour les éléments avec data-cmp-open
   */
  function initCMPOpenListeners() {
    // Écouter les clics sur les éléments avec l'attribut data-cmp-open
    document.addEventListener('click', function(event) {
      // Chercher l'élément cliqué ou son parent avec l'attribut
      const target = event.target;
      const elementWithAttribute = target.closest('[' + CMP_OPEN_ATTRIBUTE + ']');
      
      if (elementWithAttribute) {
        event.preventDefault();
        event.stopPropagation();
        openCMPBanner();
      }
    }, true); // Utiliser la capture pour intercepter tôt
  }

  /**
   * Initialise l'écoute des clics sur le bouton "Accepter"
   * Pour réinitialiser le localStorage lors du premier consentement
   */
  function initAcceptButtonListener() {
    // Utiliser la délégation d'événements pour capturer les clics même si le bouton est ajouté dynamiquement
    document.addEventListener('click', function(event) {
      const target = event.target;
      
      // Vérifier si le clic est sur le bouton "Accepter" ou un élément à l'intérieur
      const acceptButton = target.closest(ACCEPT_BUTTON_SELECTOR);
      
      if (acceptButton) {
        // Si c'est le premier consentement (la clé existe), on la supprime
        // Cela permettra au bouton "Refuser" de réapparaître lors de la prochaine ouverture
        if (isFirstConsentProcessed()) {
          localStorage.removeItem(STORAGE_KEY);
          // Réafficher immédiatement le bouton "Refuser" si la bannière est toujours ouverte
          setTimeout(function() {
            showDenyButton();
          }, 50);
        }
      }
    }, true); // Utiliser la capture pour intercepter tôt
  }

  /**
   * Vérifie si la barre Pandectes est chargée
   */
  function isPandectesLoaded() {
    // Vérifier si le bouton de refus ou l'élément de révocation existe dans le DOM
    const denyButton = document.querySelector(DENY_BUTTON_SELECTOR);
    const revokeElement = document.querySelector(REVOKE_ELEMENT_SELECTOR);
    return denyButton !== null || revokeElement !== null;
  }

  /**
   * Initialise le gestionnaire de consentement
   */
  function initCMPManager() {
    // Initialiser les listeners pour data-cmp-open dès le début
    initCMPOpenListeners();
    
    // Initialiser l'écoute du bouton "Accepter"
    initAcceptButtonListener();

    // Vérifier si le premier consentement a déjà été traité
    if (isFirstConsentProcessed()) {
      // Même si déjà traité, on doit cacher l'élément de révocation
      waitForPandectesAndHideRevoke();
      return;
    }

    // Attendre que la barre Pandectes soit chargée
    let attempts = 0;
    const maxAttempts = 50; // 5 secondes max (50 * 100ms)
    
    const checkInterval = setInterval(function() {
      attempts++;
      
      if (isPandectesLoaded()) {
        clearInterval(checkInterval);
        
        // Cacher le bouton de refus
        hideDenyButton();
        
        // Cacher l'élément de révocation
        hideRevokeElement();
        
        // Marquer comme traité pour les prochaines visites
        markFirstConsentProcessed();
        
        // Surveiller les changements dynamiques du DOM au cas où
        // la barre serait rechargée ou modifiée
        observeDOMChanges();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.warn('CMP Manager: Timeout - La barre Pandectes n\'a pas été détectée');
      }
    }, 100); // Vérifier toutes les 100ms
  }

  /**
   * Attend que Pandectes soit chargé pour cacher l'élément de révocation
   * (utilisé même si le premier consentement a déjà été traité)
   */
  function waitForPandectesAndHideRevoke() {
    let attempts = 0;
    const maxAttempts = 50;
    
    const checkInterval = setInterval(function() {
      attempts++;
      
      if (getRevokeElement()) {
        clearInterval(checkInterval);
        hideRevokeElement();
        observeDOMChanges();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
      }
    }, 100);
  }

  /**
   * Observe les changements du DOM pour détecter si la barre est rechargée
   */
  function observeDOMChanges() {
    if (!window.MutationObserver) {
      return; // Fallback pour les anciens navigateurs
    }

    const observer = new MutationObserver(function(mutations) {
      const denyButton = document.querySelector(DENY_BUTTON_SELECTOR);
      
      // Si c'est le premier consentement (clé existe), on cache le bouton "Refuser"
      if (isFirstConsentProcessed()) {
        if (denyButton && !denyButton.hasAttribute('data-cmp-hidden')) {
          hideDenyButton();
        }
      } else {
        // Si ce n'est plus le premier consentement (clé supprimée après acceptation),
        // on s'assure que le bouton "Refuser" est visible
        if (denyButton && denyButton.hasAttribute('data-cmp-hidden')) {
          showDenyButton();
        }
      }
      
      // Toujours cacher l'élément de révocation s'il réapparaît
      const revokeElement = getRevokeElement();
      if (revokeElement && !revokeElement.hasAttribute('data-cmp-hidden')) {
        hideRevokeElement();
      }
    });

    // Observer les changements dans le body
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Démarre le gestionnaire quand le DOM est prêt
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCMPManager);
  } else {
    // Le DOM est déjà chargé
    initCMPManager();
  }

  // Également démarrer après le chargement complet de la page
  // pour capturer les éléments chargés dynamiquement
  window.addEventListener('load', function() {
    // Seulement au premier chargement de la page (premier consentement)
    if (!isFirstConsentProcessed() && isPandectesLoaded()) {
      hideDenyButton();
      markFirstConsentProcessed();
    }
    // Toujours cacher l'élément de révocation
    hideRevokeElement();
  });

  // Exposer la fonction openCMPBanner globalement pour qu'elle puisse être utilisée
  // depuis d'autres scripts ou la console du navigateur
  window.openCMPBanner = openCMPBanner;

})();

